import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronUp, Globe, Lock, LogOut, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';

interface Props {
  onLogout: () => void;
  compact?: boolean;
  /** When true, popup opens above the trigger (sidebar footer). Default: opens downward. */
  menuOpensUp?: boolean;
}

const MENU_CLASS =
  'bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-1 min-w-[200px]';

export default function UserAccountMenu({ onLogout, compact, menuOpensUp }: Props) {
  const { t } = useTranslation();
  const { user, role } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [compactMenuStyle, setCompactMenuStyle] = useState<React.CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useLayoutEffect(() => {
    if (!open || !compact || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const menuWidth = 200;
      const gap = 8;
      let left = rect.right + gap;
      // Keep menu on screen when rail is near the right edge (unlikely) or use left side
      if (left + menuWidth > window.innerWidth - 8) {
        left = Math.max(8, rect.left - menuWidth - gap);
      }
      setCompactMenuStyle({
        position: 'fixed',
        left,
        bottom: Math.max(8, window.innerHeight - rect.bottom),
        zIndex: 70,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, compact]);

  const initial = user?.full_name?.[0]?.toUpperCase() || 'U';

  const menuItems = (
    <>
      <Link
        to="/account"
        onClick={() => setOpen(false)}
        className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-700/80 whitespace-nowrap"
      >
        <UserIcon className="w-4 h-4 shrink-0" /> {t('account.myProfile')}
      </Link>
      <Link
        to="/account#password"
        onClick={() => setOpen(false)}
        className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-700/80 whitespace-nowrap"
      >
        <Lock className="w-4 h-4 shrink-0" /> {t('account.passwordMenu')}
      </Link>
      <Link
        to="/account#language"
        onClick={() => setOpen(false)}
        className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-700/80 whitespace-nowrap"
      >
        <Globe className="w-4 h-4 shrink-0" /> {t('account.language')}
      </Link>
      <div className="border-t border-slate-700 my-1" />
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          onLogout();
        }}
        className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 w-full text-left whitespace-nowrap"
      >
        <LogOut className="w-4 h-4 shrink-0" /> {t('nav.logout')}
      </button>
    </>
  );

  return (
    <div ref={rootRef} className={`relative ${compact ? '' : 'w-full'}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={user?.full_name || t('account.myProfile')}
        className={`flex items-center gap-2 w-full rounded-lg transition-colors ${
          compact
            ? 'justify-center p-2 hover:bg-slate-800 text-slate-300 hover:text-white'
            : 'px-3 py-2.5 hover:bg-slate-800 text-slate-200'
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-primary-600/20 text-primary-300 flex items-center justify-center text-sm font-semibold shrink-0">
          {initial}
        </div>
        {!compact && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-sm font-medium truncate">{user?.full_name || 'User'}</p>
              <p className="text-xs text-slate-400 truncate">{role}</p>
            </div>
            <ChevronUp className={`w-4 h-4 text-slate-400 transition-transform ${open ? '' : 'rotate-180'}`} />
          </>
        )}
      </button>

      {open && compact && createPortal(
        <div ref={menuRef} style={compactMenuStyle} className={MENU_CLASS}>
          {menuItems}
        </div>,
        document.body,
      )}

      {open && !compact && (
        <div
          ref={menuRef}
          className={`absolute z-50 ${MENU_CLASS} ${
            menuOpensUp
              ? 'bottom-full left-0 right-0 mb-2'
              : 'top-full left-0 right-0 mt-2'
          }`}
        >
          {menuItems}
        </div>
      )}
    </div>
  );
}
